/**
 * Project themes: one file (`ui/theme.json` by default) with the project's
 * named colours, fonts, text styles, button styles, panels and bars, so the
 * whole game is restyled in one place.
 *
 * Objects pick a style by name with `themeStyle`, and keep only what they
 * change, the way a prefab placement keeps overrides:
 *
 *   { "id": "t4", "type": "text", "text": "Hint", "themeStyle": "Body", "style": { "color": "Danger" } }
 *
 * Text styles use Phaser's own TextStyle keys, so a text object's `style`
 * overrides them key by key. Wherever a style takes a colour, it can name one
 * of the theme's colours instead of giving a hex value.
 *
 * This module has the file's types and the lookups that need no Phaser. The
 * runtime's Theme turns them into Phaser styles and objects.
 */
import type { TextStyle } from './types.js';

/** Where a project keeps its theme when project.json names none. */
export const THEME_FILE = 'ui/theme.json';

export interface ThemeFile {
  formatVersion: number;
  /**
   * How text is drawn. `smooth` renders text at its on-screen size, so web
   * fonts stay sharp when the canvas is scaled up. `pixel` keeps text on the
   * game's pixel grid, for pixel-art games with pixel or bitmap fonts. Default smooth.
   */
  rendering?: 'smooth' | 'pixel';
  /** Named colours, as `#rrggbb` or `#rrggbbaa`. */
  colors?: Record<string, string>;
  /** Fonts by family name, which text styles use as `fontFamily`. */
  fonts?: Record<string, ThemeFont>;
  text?: Record<string, ThemeTextStyle>;
  buttons?: Record<string, ThemeButtonStyle>;
  panels?: Record<string, ThemePanelStyle>;
  bars?: Record<string, ThemeBarStyle>;
  /** The style new objects of each kind start with. */
  defaults?: { text?: string; button?: string; panel?: string; bar?: string };
}

/**
 * A font the game loads before its first scene. With `url`, a web font file
 * (woff2, woff, ttf or otf); with `bitmap`, a bitmap font (its BMFont XML
 * file, and its texture); with neither, a font the player's system has.
 */
export interface ThemeFont {
  url?: string;
  /** CSS weight of the file, such as `600`. */
  weight?: string;
  /** CSS style of the file, such as `italic`. */
  style?: string;
  /** Path of a bitmap font's BMFont XML file (`.xml` or `.fnt`). */
  bitmap?: string;
  /** Path of a bitmap font's texture. */
  texture?: string;
  /** A bitmap font's size in pixels, for scaling it to a style's fontSize. */
  size?: number;
}

/** A text style: Phaser's TextStyle keys, where colours may name theme colours. */
export type ThemeTextStyle = TextStyle;

/** How a button looks in one state. */
export interface ThemeButtonState {
  /** Panel style drawn behind the label. */
  panel?: string;
  /** Text style of the label. */
  label?: string;
  /** Colour of the label, replacing its text style's colour. */
  labelColor?: string;
  /** Scale of the whole button, such as 1.06 when hovered. */
  scale?: number;
  /** Space between the label and the panel's edge. */
  padding?: ThemePadding;
  /** A sound from the sounds file, by name, played as the button enters the state (hover or pressed). Texture buttons with this style play it too. */
  sound?: string;
  alpha?: number;
}

export interface ThemeButtonStyle extends ThemeButtonState {
  /** What changes from the normal state while the pointer is over it. */
  hover?: ThemeButtonState;
  pressed?: ThemeButtonState;
  disabled?: ThemeButtonState;
}

export type ButtonState = 'normal' | 'hover' | 'pressed' | 'disabled';
export const BUTTON_STATES: readonly ButtonState[] = ['normal', 'hover', 'pressed', 'disabled'];

export interface ThemePadding {
  x?: number;
  y?: number;
}

/**
 * A panel: a rounded rectangle drawn from colours, or with `texture`, a
 * nine-slice image whose corners keep their size as it stretches.
 */
export interface ThemePanelStyle {
  fill?: string;
  /** 0 to 1, on top of the fill colour's own alpha. Default 1. */
  fillAlpha?: number;
  stroke?: string;
  strokeWidth?: number;
  radius?: number;
  texture?: { key: string; frame?: string | number };
  /** Nine-slice insets of the texture, in pixels. */
  slice?: { left: number; right: number; top: number; bottom: number };
  /** Space between the panel's edge and what it holds. */
  padding?: ThemePadding;
  /** What changes while the panel is selected, such as a picked inventory slot. */
  selected?: Omit<ThemePanelStyle, 'selected'>;
}

/** A bar, such as health: a panel behind, and a fill that grows with the value. */
export interface ThemeBarStyle {
  /** Panel style behind the fill. */
  panel?: string;
  /** Colour of the fill. */
  fill?: string;
  /** A panel style for the fill, instead of a plain colour. */
  fillPanel?: string;
  /** Gap between the panel's edge and the fill. Default 0. */
  inset?: number;
  /** Which way the fill grows. Default right. */
  direction?: 'right' | 'left' | 'up' | 'down';
}

/** The sections of a theme that hold named styles, and the kind of object each styles. */
export const THEME_SECTIONS = ['colors', 'fonts', 'text', 'buttons', 'panels', 'bars'] as const;
export type ThemeSection = (typeof THEME_SECTIONS)[number];

/** Which theme section an object type's `themeStyle` names. */
export function styleSectionFor(type: string): ThemeSection | undefined {
  if (type === 'text' || /(^|\.)bitmapText$/i.test(type)) return 'text';
  if (/(^|\.)(texture)?button$/i.test(type)) return 'buttons';
  if (/(^|\.)(panel|nineSlice|stack)$/i.test(type)) return 'panels';
  if (/(^|\.)bar$/i.test(type)) return 'bars';
  return undefined;
}

const DEFAULT_KEY: Partial<Record<ThemeSection, 'text' | 'button' | 'panel' | 'bar'>> = { text: 'text', buttons: 'button', panels: 'panel', bars: 'bar' };

/** The style new objects of a type start with, if the theme has one. */
export function defaultStyleFor(theme: ThemeFile | undefined, type: string): string | undefined {
  const section = styleSectionFor(type);
  const key = section && DEFAULT_KEY[section];
  if (!theme || !section || !key) return undefined;
  const name = theme.defaults?.[key];
  return name && (theme[section] as Record<string, unknown> | undefined)?.[name] ? name : undefined;
}

/** The project's theme path: project.json's `theme`, else THEME_FILE. */
export function themePathOf(project: { theme?: string } | undefined): string {
  return project?.theme || THEME_FILE;
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value);
}

/**
 * A colour as CSS: a theme colour's value when `value` names one, else
 * `value` itself. Undefined stays undefined.
 */
export function themeColor(theme: ThemeFile | undefined, value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return theme?.colors?.[value] ?? value;
}

/** A `#rgb[a]` or `#rrggbb[aa]` colour as 0xRRGGBB and an alpha from 0 to 1. Undefined for anything else. */
export function parseColor(css: string | undefined): { rgb: number; alpha: number } | undefined {
  if (!isHexColor(css)) return undefined;
  let hex = css.slice(1);
  if (hex.length <= 4) hex = [...hex].map((c) => c + c).join('');
  return { rgb: parseInt(hex.slice(0, 6), 16), alpha: hex.length === 8 ? parseInt(hex.slice(6), 16) / 255 : 1 };
}

/** TextStyle keys that hold a colour. */
const TEXT_COLOR_KEYS = ['color', 'stroke', 'backgroundColor'] as const;

/**
 * A text style ready for Phaser: the theme's style `name` (if the theme has
 * it), with `overrides` on top, colour names swapped for their values and a
 * numeric fontSize written in pixels.
 */
export function resolveTextStyle(theme: ThemeFile | undefined, name: string | undefined, overrides?: TextStyle): TextStyle {
  const base = (name && theme?.text?.[name]) || {};
  const out: TextStyle = { ...structuredClone(base), ...structuredClone(overrides ?? {}) };
  // A shadow given in part keeps the rest of the style's shadow.
  if (isObject(base.shadow) && isObject(overrides?.shadow)) out.shadow = { ...base.shadow, ...overrides.shadow };
  for (const key of TEXT_COLOR_KEYS) {
    if (typeof out[key] === 'string') out[key] = themeColor(theme, out[key] as string);
  }
  if (isObject(out.shadow) && typeof out.shadow.color === 'string') out.shadow = { ...out.shadow, color: themeColor(theme, out.shadow.color) };
  if (typeof out.fontSize === 'number') out.fontSize = `${out.fontSize}px`;
  return out;
}

/** A button style's look in one state: the normal look with that state's changes on top. */
export function buttonState(theme: ThemeFile | undefined, name: string | undefined, state: ButtonState = 'normal'): ThemeButtonState | undefined {
  const style = name ? theme?.buttons?.[name] : undefined;
  if (!style) return undefined;
  const { hover, pressed, disabled, ...normal } = style;
  const extra = state === 'hover' ? hover : state === 'pressed' ? pressed : state === 'disabled' ? disabled : undefined;
  return { ...normal, ...extra, ...(normal.padding && extra?.padding && { padding: { ...normal.padding, ...extra.padding } }) };
}

/** A panel style, with its selected look on top when `selected`. */
export function panelStyle(theme: ThemeFile | undefined, name: string | undefined, selected = false): ThemePanelStyle | undefined {
  const style = name ? theme?.panels?.[name] : undefined;
  if (!style) return undefined;
  const { selected: sel, ...base } = style;
  return selected && sel ? { ...base, ...sel } : base;
}

/** The colour references a style holds, by where they are in it. */
export function colorRefs(section: ThemeSection, style: unknown): { path: string[]; value: string }[] {
  const out: { path: string[]; value: string }[] = [];
  const s = style as Record<string, any> | undefined;
  if (!isObject(s)) return out;
  const add = (path: string[], v: unknown) => typeof v === 'string' && out.push({ path, value: v });
  if (section === 'text') {
    for (const k of TEXT_COLOR_KEYS) add([k], s[k]);
    if (isObject(s.shadow)) add(['shadow', 'color'], s.shadow.color);
  } else if (section === 'buttons') {
    add(['labelColor'], s.labelColor);
    for (const st of ['hover', 'pressed', 'disabled']) if (isObject(s[st])) add([st, 'labelColor'], s[st].labelColor);
  } else if (section === 'panels') {
    add(['fill'], s.fill);
    add(['stroke'], s.stroke);
    if (isObject(s.selected)) {
      add(['selected', 'fill'], s.selected.fill);
      add(['selected', 'stroke'], s.selected.stroke);
    }
  } else if (section === 'bars') {
    add(['fill'], s.fill);
  }
  return out;
}

/** The other styles a style names: a button's panels and label styles, a bar's panels. */
export function styleRefs(section: ThemeSection, style: unknown): { path: string[]; section: ThemeSection; value: string }[] {
  const out: { path: string[]; section: ThemeSection; value: string }[] = [];
  const s = style as Record<string, any> | undefined;
  if (!isObject(s)) return out;
  const add = (path: string[], to: ThemeSection, v: unknown) => typeof v === 'string' && out.push({ path, section: to, value: v });
  if (section === 'buttons') {
    for (const st of [undefined, 'hover', 'pressed', 'disabled'] as const) {
      const part = st ? s[st] : s;
      if (!isObject(part)) continue;
      const at = st ? [st] : [];
      add([...at, 'panel'], 'panels', part.panel);
      add([...at, 'label'], 'text', part.label);
    }
  } else if (section === 'bars') {
    add(['panel'], 'panels', s.panel);
    add(['fillPanel'], 'panels', s.fillPanel);
  } else if (section === 'text') {
    add(['fontFamily'], 'fonts', s.fontFamily);
  }
  return out;
}

/** A starting theme: a few colours and one style of each kind, for a project that has none. */
export function newThemeFile(): ThemeFile {
  return {
    formatVersion: 1,
    rendering: 'smooth',
    colors: { Paper: '#f3ead8', Ink: '#1b1622', Accent: '#c49bff', Danger: '#e0607a' },
    fonts: {},
    text: {
      Title: { fontFamily: 'Georgia', fontSize: 24, fontStyle: 'bold', color: 'Paper' },
      Body: { fontFamily: 'sans-serif', fontSize: 12, color: 'Paper' },
      Caption: { fontFamily: 'monospace', fontSize: 9, color: 'Accent', letterSpacing: 1 },
      Dialogue: { fontFamily: 'sans-serif', fontSize: 12, color: 'Paper', align: 'center' },
    },
    buttons: {
      Primary: { panel: 'Button', label: 'Body', padding: { x: 12, y: 6 }, hover: { scale: 1.06 }, pressed: { scale: 0.97 }, disabled: { alpha: 0.5 } },
    },
    panels: {
      Button: { fill: '#3b2f4f', stroke: 'Accent', strokeWidth: 1, radius: 6 },
      'Dialog panel': { fill: 'Ink', fillAlpha: 0.93, stroke: 'Accent', strokeWidth: 1, radius: 6, padding: { x: 8, y: 5 } },
      'Inventory slot': { fill: 'Ink', fillAlpha: 0.8, stroke: '#4a3f58', strokeWidth: 1, radius: 4, padding: { x: 6, y: 4 }, selected: { stroke: '#b9d86a', strokeWidth: 2 } },
    },
    bars: { 'Health bar': { panel: 'Dialog panel', fill: 'Danger', inset: 2 } },
    defaults: { text: 'Body', button: 'Primary', panel: 'Dialog panel', bar: 'Health bar' },
  };
}

function isObject(v: unknown): v is Record<string, any> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
